import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { masterChartPath } from 'constants/masterChartRoutes';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  ArrowBackRounded,
  AddRounded,
  EditRounded,
  DeleteRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import { contentCardSx } from 'styles/standaloneLayoutTokens';
import ConfirmDialog from 'components/common/ConfirmDialog';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import ProvenanceInfoButton from 'components/individual/health-records/ProvenanceInfoButton';
import { useAsyncAutocomplete } from 'hooks/useAsyncAutocomplete';
import { rxTermsService } from 'services/rxTermsService';
import {
  CMR_CONFIRM_QUERY,
  clearCmrPatientConfirmProposal,
  normalizeProposedAllergies,
  readCmrPatientConfirmProposal,
} from 'services/cmr/cmrPatientConfirmOutbound';

const ALLERGY_TYPES = [
  { value: 'drug', label: 'Drug / Medication' },
  { value: 'food', label: 'Food' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'other', label: 'Other' },
];

const COMMON_ALLERGENS = {
  drug: ['Penicillin', 'Sulfa Drugs', 'Aspirin', 'NSAIDs', 'Codeine', 'Morphine', 'Iodine Contrast', 'Latex'],
  food: ['Peanuts', 'Tree Nuts', 'Shellfish', 'Fish', 'Eggs', 'Milk', 'Soy', 'Wheat'],
  environmental: ['Pollen', 'Dust Mites', 'Pet Dander', 'Mold', 'Bee Stings', 'Latex'],
  other: [],
};

const AllergiesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);

  const [allergies, setAllergies] = useState([]);
  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [selectedRxcui, setSelectedRxcui] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmProposal, setConfirmProposal] = useState(null);
  const [confirmingProposal, setConfirmingProposal] = useState(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { allergen: '', type: 'drug', reaction: '', notes: '' },
  });

  const watchedType = watch('type');
  const watchedAllergen = watch('allergen');

  const searchDrugs = useCallback(
    (q) => rxTermsService.searchMedications(q, 15),
    []
  );
  const { options: drugOptions, loading: drugLoading } = useAsyncAutocomplete(
    watchedType === 'drug' ? watchedAllergen : '',
    searchDrugs,
    { minLength: 2 }
  );

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const doc = await db.collection('users').doc(user.uid).get();
        const hr = doc.data()?.healthRecords || {};
        setFullHealthRecords(hr);
        setAllergies(hr.allergies || []);
      } catch {
        setLoadError(
          'Unable to load allergy information — retry before assuming you have none.',
        );
        setAllergies([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  // Deep-link / sessionStorage prefill for confirmRequired-by-patient outbound.
  useEffect(() => {
    const wantsConfirm = searchParams.get(CMR_CONFIRM_QUERY) === '1';
    const fieldParam = (searchParams.get('field') || '').trim();
    if (!wantsConfirm && !fieldParam) return;

    const proposal = readCmrPatientConfirmProposal();
    if (
      proposal &&
      proposal.field === 'allergies' &&
      (!fieldParam || fieldParam === 'allergies')
    ) {
      setConfirmProposal(proposal);
    }
  }, [searchParams]);

  const clearConfirmQuery = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(CMR_CONFIRM_QUERY);
    next.delete('field');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleDeclineProposal = () => {
    clearCmrPatientConfirmProposal();
    setConfirmProposal(null);
    clearConfirmQuery();
    setSuccessMsg('Proposed allergy update declined. Your health record was not changed.');
  };

  const saveToFirestore = useCallback(
    async (updated) => {
      const merged = {
        ...(fullHealthRecords ?? {}),
        allergies: updated,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
    },
    [user?.uid, fullHealthRecords]
  );

  const handleAcceptProposal = async () => {
    if (!confirmProposal || !user?.uid) return;
    setConfirmingProposal(true);
    setError(null);
    try {
      const proposed = normalizeProposedAllergies(confirmProposal.proposedValue);
      if (!proposed.length) {
        setError('No valid allergy was proposed to confirm.');
        return;
      }
      const byKey = new Map(
        allergies.map((row) => [
          `${(row.allergen || '').toLowerCase()}|${row.type || ''}`,
          row,
        ]),
      );
      proposed.forEach((row) => {
        byKey.set(`${row.allergen.toLowerCase()}|${row.type || ''}`, row);
      });
      const updated = Array.from(byKey.values()).sort((a, b) =>
        a.allergen.localeCompare(b.allergen),
      );
      await saveToFirestore(updated);
      setAllergies(updated);
      clearCmrPatientConfirmProposal();
      setConfirmProposal(null);
      clearConfirmQuery();
      setSuccessMsg('Allergy update confirmed and saved to your health record.');
    } catch {
      setError('Failed to save confirmed allergies. Please try again.');
    } finally {
      setConfirmingProposal(false);
    }
  };

  const openDialog = (index = -1) => {
    setEditingIndex(index);
    if (index >= 0) {
      const a = allergies[index];
      reset({
        allergen: a.allergen ?? '',
        type: a.type ?? 'drug',
        reaction: a.reaction ?? '',
        notes: a.notes ?? '',
      });
      setSelectedRxcui(a.rxcui ?? '');
    } else {
      reset({ allergen: '', type: 'drug', reaction: '', notes: '' });
      setSelectedRxcui('');
    }
    setDialogOpen(true);
  };

  const onDialogSubmit = handleSubmit(async (data) => {
    setSaving(true);
    setError(null);
    try {
      const toSave = {
        allergen: data.allergen.trim(),
        type: data.type,
        reaction: data.reaction.trim(),
        notes: data.notes.trim(),
        ...(data.type === 'drug' && selectedRxcui ? { rxcui: selectedRxcui } : {}),
      };
      const updated = [...allergies];
      if (editingIndex >= 0) {
        updated[editingIndex] = toSave;
      } else {
        updated.push(toSave);
      }
      updated.sort((a, b) => a.allergen.localeCompare(b.allergen));
      await saveToFirestore(updated);
      setAllergies(updated);
      setDialogOpen(false);
      setSuccessMsg(editingIndex >= 0 ? 'Allergy updated.' : 'Allergy added.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const updated = allergies.filter((_, i) => i !== pendingDelete.index);
      setAllergies(updated);
      await saveToFirestore(updated);
      setSuccessMsg(`Removed "${pendingDelete.label}".`);
      setPendingDelete(null);
    } catch {
      setError('Failed to remove. Please try again.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const markNoKnownAllergies = async () => {
    const entry = [{ allergen: 'No Known Drug Allergies', type: 'drug', reaction: '', notes: '' }];
    setAllergies(entry);
    try {
      await saveToFirestore(entry);
      setSuccessMsg('Noted: No Known Drug Allergies.');
    } catch {
      setError('Failed to save. Please try again.');
    }
  };

  const typeLabel = (type) =>
    ALLERGY_TYPES.find((t) => t.value === type)?.label ?? type;

  return (
    <div className="inner">
      <Box sx={{ mt: { xs: 1, sm: 5 }, mb: 1 }}>
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate(masterChartPath())}
          size="small"
        >
          Back to Health Records
        </Button>
      </Box>
      <DashboardPageHeader
        title="Allergies"
        subtitle="Drug, food, and environmental allergies"
        sx={{ mt: 0 }}
        actions={
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => openDialog()}
          >
            Add Allergy
          </Button>
        }
      />

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {confirmProposal ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="cmr-patient-confirm-banner"
          action={
            <Stack direction="row" spacing={1}>
              <LoadingButton
                color="inherit"
                size="small"
                loading={confirmingProposal}
                onClick={handleAcceptProposal}
                data-testid="cmr-patient-confirm-accept"
              >
                Confirm & save
              </LoadingButton>
              <Button
                color="inherit"
                size="small"
                onClick={handleDeclineProposal}
                data-testid="cmr-patient-confirm-decline"
              >
                Decline
              </Button>
            </Stack>
          }
        >
          A care session proposed an allergy update via SecureLink. Confirm to write
          it to your health record, or decline to leave your record unchanged.
          {normalizeProposedAllergies(confirmProposal.proposedValue).length ? (
            <Box
              component="span"
              sx={{ display: 'block', mt: 0.5 }}
              data-testid="cmr-patient-confirm-proposed"
            >
              Proposed:{' '}
              {normalizeProposedAllergies(confirmProposal.proposedValue)
                .map((row) => row.allergen)
                .join(', ')}
            </Box>
          ) : null}
        </Alert>
      ) : null}

      {allergies.length === 0 && !loading && !loadError && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={markNoKnownAllergies}>
              No known drug allergies
            </Button>
          }
        >
          No allergies to add? You can note that you have no known drug allergies so your care team is aware.
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...contentCardSx, mb: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" gutterBottom>
              Allergy list unavailable until the load succeeds.
            </Typography>
          </Box>
        ) : allergies.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <WarningAmberRounded sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>
              No allergies added yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your known allergies, or use the button above if you have none.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Allergen</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reaction</TableCell>
                  <TableCell sx={{ width: 88 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {allergies.map((a, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{a.allergen}</Typography>
                        <ProvenanceInfoButton source={a.source} />
                      </Box>
                      {a.notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {a.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={typeLabel(a.type)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{a.reaction || ''}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        onClick={() => openDialog(i)}
                        aria-label={`Edit allergy: ${a.allergen}`}
                      >
                        <EditRounded fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setPendingDelete({ index: i, label: a.allergen })}
                        aria-label={`Delete allergy: ${a.allergen}`}
                      >
                        <DeleteRounded fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingIndex >= 0 ? 'Edit Allergy' : 'Add Allergy'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="allergy-type-label">Allergy Type</InputLabel>
                  <Select
                    {...field}
                    labelId="allergy-type-label"
                    label="Allergy Type"
                    onChange={(e) => {
                      field.onChange(e);
                      setValue('allergen', '');
                      setSelectedRxcui('');
                    }}
                  >
                    {ALLERGY_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            {watchedType === 'drug' ? (
              <Controller
                name="allergen"
                control={control}
                rules={{ required: 'Allergen is required' }}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <Autocomplete
                    freeSolo
                    options={drugOptions}
                    loading={drugLoading}
                    getOptionLabel={(opt) =>
                      typeof opt === 'string' ? opt : opt?.name ?? opt?.displayName ?? ''
                    }
                    inputValue={field.value || ''}
                    onInputChange={(_, value, reason) => {
                      if (reason !== 'reset') {
                        field.onChange(value);
                        if (reason === 'clear' || reason === 'input') setSelectedRxcui('');
                      }
                    }}
                    onChange={(_, newValue) => {
                      if (!newValue) return;
                      if (typeof newValue === 'string') {
                        field.onChange(newValue);
                        setSelectedRxcui('');
                      } else {
                        field.onChange(newValue.name || newValue.displayName || '');
                        setSelectedRxcui(newValue.rxcui ? String(newValue.rxcui) : '');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Drug / Allergen"
                        placeholder="e.g., Penicillin"
                        error={!!fieldError}
                        helperText={
                          fieldError?.message ??
                          'Standard drugs support drug-allergy interaction checks.'
                        }
                      />
                    )}
                  />
                )}
              />
            ) : (
              <Controller
                name="allergen"
                control={control}
                rules={{ required: 'Allergen is required' }}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <TextField
                    {...field}
                    label="Allergen"
                    fullWidth
                    placeholder={
                      watchedType === 'food' ? 'e.g., Peanuts' : 'e.g., Pollen'
                    }
                    error={!!fieldError}
                    helperText={fieldError?.message}
                  />
                )}
              />
            )}

            {(COMMON_ALLERGENS[watchedType]?.length ?? 0) > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                >
                  Common allergens:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {COMMON_ALLERGENS[watchedType].map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setValue('allergen', name);
                        setSelectedRxcui('');
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Controller
              name="reaction"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reaction"
                  fullWidth
                  placeholder="e.g., Hives, Anaphylaxis, Rash"
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Additional details (optional)"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={onDialogSubmit}
            loading={saving}
            disabled={saving}
          >
            {editingIndex >= 0 ? 'Update' : 'Add'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove Allergy"
        message={`Remove "${pendingDelete?.label}"?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onExited={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default AllergiesPage;
